import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import Sidebar from '../components/Sidebar';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';

// Employee Dashboard Components
const EmployeeOverview = lazy(() => import('../components/employee/EmployeeOverview'));
const LeaveRequestForm = lazy(() => import('../components/employee/LeaveRequestForm'));
const MyRequests = lazy(() => import('../components/employee/MyRequests'));
const PermissionForm = lazy(() => import('../components/employee/PermissionForm'));
const MyPermissions = lazy(() => import('../components/employee/MyPermissions'));
const ClaimForm = lazy(() => import('../components/employee/ClaimForm'));
const MyClaims = lazy(() => import('../components/employee/MyClaims'));
const Attendance = lazy(() => import('../components/employee/Attendance'));
const Refreshments = lazy(() => import('../components/employee/Refreshments'));
const Profile = lazy(() => import('../components/employee/Profile'));
const TaskSubmission = lazy(() => import('../components/employee/TaskSubmission'));
const DailyTask = lazy(() => import('../components/employee/DailyTask'));
const RequestWork = lazy(() => import('../components/employee/RequestWork'));

const EmployeeDashboard = () => {
  const location = useLocation();
  const { user, api } = useAuth();
  const [sidebarShow, setSidebarShow] = useState(false);
  const [stats, setStats] = useState({
    totalRequests: 0,
    pendingRequests: 0,
    approvedRequests: 0,
    rejectedRequests: 0
  });
  const [loading, setLoading] = useState(true);

  // Determine active menu item based on current path
  const getActiveItem = () => {
    const path = location.pathname;
    if (path === '/employee' || path === '/employee/') return 'dashboard';
    if (path.includes('/leave-request')) return 'leave-request';
    if (path.includes('/my-requests')) return 'my-requests';
    if (path.includes('/permission-request')) return 'permission-request';
    if (path.includes('/my-permissions')) return 'my-permissions';
    if (path === '/employee/claims') return 'claims';
    if (path.includes('/my-claims')) return 'my-claims';
    if (path.includes('/attendance')) return 'attendance';
    if (path.includes('/refreshments')) return 'refreshments';
    if (path.includes('/task-submission')) return 'task-submission';
    if (path.includes('/daily-task')) return 'daily-task';
    if (path.includes('/request-work')) return 'request-work';
    if (path.includes('/profile')) return 'profile';
    return 'dashboard';
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      
      // Fetch employee's leave requests
      const response = await api.get('/leave-requests');
      const requests = response.data.leaveRequests || [];
      
      setStats({
        totalRequests: requests.length,
        pendingRequests: requests.filter(req => req.status === 'pending').length,
        approvedRequests: requests.filter(req => req.status === 'approved').length,
        rejectedRequests: requests.filter(req => req.status === 'rejected').length
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSidebar = () => {
    setSidebarShow(!sidebarShow);
  };

  if (loading && location.pathname === '/employee') {
    return <LoadingSpinner message="Loading dashboard..." />;
  }

  return (
    <div className="dashboard-container">
      <Sidebar 
        activeItem={getActiveItem()} 
        userRole="employee" 
        show={sidebarShow}
        onClose={() => setSidebarShow(false)}
      />
      
      {/* Mobile sidebar toggle */}
      <Button
        variant="primary"
        className="d-lg-none position-fixed mobile-menu-toggle"
        style={{ top: '15px', left: '15px', zIndex: 1001 }}
        onClick={toggleSidebar}
      >
        <i className="fas fa-bars"></i>
      </Button>

      <div className="main-content">
        <Suspense fallback={<LoadingSpinner message="Loading page..." />}>
          <Routes>
            <Route 
              path="/" 
              element={<EmployeeOverview stats={stats} refreshStats={fetchStats} />} 
            />
            <Route 
              path="/leave-request" 
              element={<LeaveRequestForm onRequestSubmitted={fetchStats} />} 
            />
            <Route 
              path="/my-requests" 
              element={<MyRequests refreshStats={fetchStats} />} 
            />
            <Route 
              path="/permission-request" 
              element={<PermissionForm />} 
            />
            <Route 
              path="/my-permissions" 
              element={<MyPermissions />} 
            />
            <Route 
              path="/claims" 
              element={<ClaimForm />} 
            />
            <Route 
              path="/my-claims" 
              element={<MyClaims />} 
            />
            <Route 
              path="/attendance" 
              element={<Attendance />} 
            />
            <Route 
              path="/refreshments" 
              element={<Refreshments />} 
            />
            <Route 
              path="/task-submission" 
              element={<TaskSubmission />} 
            />
            <Route 
              path="/daily-task" 
              element={<DailyTask />} 
            />
            <Route 
              path="/request-work" 
              element={<RequestWork />} 
            />
            <Route 
              path="/profile" 
              element={<Profile />} 
            />
          </Routes>
        </Suspense>
      </div>
    </div>
  );
};

export default EmployeeDashboard;